import React, { useRef, useEffect, useState, MouseEventHandler } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import Icon from 'components/commons/icon/Icon';
import UserBrokerList from 'components/features/security-accounts/UserBrokerList';
import OKCanelModal from 'components/features/modal/OKCancelModal';
import { C_UpdateDataInfo } from 'components/features/UpdateDataInfo';
import GoMoneytreeSiteDialog from 'components/features/MoveMoneytreeSiteDialog';
import { LineNoticeBanner } from 'components/features/home/HomeContentsInternal';
import { AboutExchangeRateModalProvider } from 'components/features/modal/AboutExchangeRateModal';
import { AboutExchangeRateModalButton } from 'components/features/home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import { InformationFromMoneytreeModal } from 'components/features/modal/InformationFromMoneytreeModal';
import { AboutDataUpdateTimingModal } from 'components/features/modal/AboutDataUpdateTimingModal';
import { AccountUpdateStoppedModal } from 'components/features/modal/AccountUpdateStoppedModal';
import { NoticeSettingModal } from 'components/features/modal/NoticeSettingModal';
import { getUserStorage } from 'components/features/utils/utils';
import {
  isPremium,
  isIosApp,
  isIosAppNotPremium,
} from 'components/features/utils/userStatus';
import MessageModal from 'components/features/modal/MessageModal';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';

import * as env from 'config/environment';
import * as consts from 'config/consts';
import * as types from 'config/types';

import {
  getUserBroker as getUserBroker_,
  GetUserBrokerResultItem,
  getMissions,
  getUser,
  GetUserResut,
  unlinkMoneytree,
  refreshMoneytree,
  GetUserBrokerResult,
} from 'config/api';
import { formatDateForString } from 'utils/formatter';
import { debounce } from 'utils/debounce';
import { getViewedWebPushStorage } from 'utils/storage';
import { useLoading } from 'hooks/useLoadign';
import { isLoaded, useGrouping } from 'hooks/useGrouping';
import { useModal } from 'hooks/useModal';

const getUserBroker = async (): Promise<GetUserBrokerResult> => {
  const isDebug = false;
  if (isDebug)
    return {
      Items: [],
    };
  return await getUserBroker_();
};

const securityCompanies = [
  {
    brokerID: consts.MONOBrokerIds.SBI証券,
    name: 'SBI証券',
    url:
      'https://minkabu.jp/hikaku/redirect_tw/sbi.html?utm_source=tw&utm_medium=securities_list&utm_campaign=',
  },
  {
    brokerID: consts.MONOBrokerIds.楽天証券,
    name: '楽天証券',
    url:
      'https://minkabu.jp/hikaku/redirect_tw/rakuten.html?utm_source=tw&utm_medium=securities_list&utm_campaign=',
  },
  {
    brokerID: consts.MONOBrokerIds.松井証券,
    name: '松井証券',
    url:
      'https://minkabu.jp/hikaku/redirect_tw/matsui.html?utm_source=tw&utm_medium=securities_list&utm_campaign=',
  },
  {
    brokerID: consts.MONOBrokerIds.三菱UFJeスマート証券,
    name: '三菱UFJ eスマート証券',
    url:
      'https://minkabu.jp/hikaku/redirect_tw/kabucom.html?utm_source=tw&utm_medium=securities_list&utm_campaign=',
  },
];
const securityRankingUrl =
  'https://minkabu.jp/hikaku/lp/total_ranking_tw.html?utm_source=tw&utm_medium=securities_list&utm_campaign=';

const calcMaxHeight = (extracted: boolean): string => {
  const zero = '0px';
  if (!extracted) return zero;

  const panelHeight =
    document.getElementById('moneytree_panel')?.offsetHeight ?? 0;
  return `${panelHeight}px`;
};

const SecurityAccounts: React.FC = () => {
  const { setIsShowMenu } = useGlobalMenuContext();

  const [brokerItems, setBrokerItems] = useState<Array<
    GetUserBrokerResultItem
  > | null>(null);
  const [user, setUser] = useState<GetUserResut>();
  const [shownUnlinkConfirmDialog, setShownUnlinkConfirmDialog] = useState(
    false
  );
  const [extractedMoneytree, setExtractedMoneytree] = useState<boolean>(false);
  const [
    showGoMoneytreeSiteDialogUrl,
    setShowGoMoneytreeSiteDialogUrl,
  ] = useState<string>('');
  const [beInRefresh, setBeInRefresh] = useState<boolean>(false);

  const [showModalByEditing, setShowModalByEditing] = useState<boolean>(false);
  const [
    isShowInformationFromMoneytreeModal,
    {
      hideModal: hideInformationFromMoneytreeModal,
      showModal: showInformationFromMoneytreeModal,
    },
  ] = useModal();

  const [
    isShowAccountAggregatingModal,
    {
      hideModal: hideAccountAggregatingModal,
      showModal: showAccountAggregatingModal,
    },
  ] = useModal();

  const [
    isShowAccountUpdateStoppedModal,
    {
      hideModal: hideAccountUpdateStoppedModal,
      showModal: showAccountUpdateStoppedModal,
    },
  ] = useModal();

  const urlParams = new URLSearchParams(window.location.search);
  const webPushModalJudge = urlParams.get('webPushModalJudge');

  const userAgent = window.navigator.userAgent.toLowerCase();
  const isiPhone = /iphone|ipod/.test(userAgent);
  const isiPad = /ipad|macintosh/.test(userAgent) && 'ontouchend' in document;

  const userStorage = getUserStorage();
  const viewedWebPushStorage = getViewedWebPushStorage();
  const initialValue =
    !isiPhone &&
    !isiPad &&
    webPushModalJudge === 'true' &&
    userStorage !== null &&
    !userStorage.RegisteredWebPush &&
    (viewedWebPushStorage === null || !viewedWebPushStorage.securityAccounts);

  const [
    isShowNoticeSettingModal,
    { hideModal: hideNoticeSettingModal },
  ] = useModal(initialValue);

  useEffect(() => {
    return setIsShowMenu(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 追従ボタンがstickyか否かでスタイルが異なるため対応、要共通化
  useEffect(() => {
    const securityAccountScrollDebounce = debounce(() => {
      const accountAddBtnPosition = document
        .getElementById('accountAddBtn')
        ?.getBoundingClientRect().bottom;
      const viewHeight = window.innerHeight;
      if (accountAddBtnPosition === undefined) {
        return null;
      } else if (accountAddBtnPosition === viewHeight) {
        document
          .getElementById('accountAddBtn')
          ?.classList.add('bg-op-w80', 'shadow-fBtn');
      } else {
        document
          .getElementById('accountAddBtn')
          ?.classList.remove('bg-op-w80', 'shadow-fBtn');
      }
    }, 16);
    window.addEventListener('scroll', securityAccountScrollDebounce, true);

    return () => {
      window.removeEventListener('scroll', securityAccountScrollDebounce, true);
    };
  }, []);

  /* 
    HACK: useLoadingと統合する
    統合するには、画面全体をマスクする分岐が必要
    全体に影響が出るので、時間があるときに！
  */
  const unmounted = useRef<boolean>(false);

  const history = useHistory();

  const [, { disableLoading }] = useLoading(true);

  const { setAction } = useCustomizeHeaderContext();

  const [groupingState, { getGroupingAPI }] = useGrouping();

  const moneytreeSettingURL = encodeURI(
    `${env.moneytreeBaseURL}/settings/?client_id=${env.moneytreeClientId}`
  );
  const moneytreeManagementURL = encodeURI(
    `${env.moneytreeManagementURL}?client_id=${env.moneytreeClientId}`
  );

  const doUnlinkOK = (): void => {
    unlinkMoneytree().then(ret => {
      document.location.reload();
    });
  };
  const doUnlinkCancel = (): void => {
    setShownUnlinkConfirmDialog(false);
  };

  useEffect(() => {
    getUserBroker().then(ret => {
      !unmounted.current && ret && setBrokerItems(ret.Items);
    });

    getMissions();

    getUser().then(ret => {
      !unmounted.current && ret && setUser(ret);
    });

    (async () => {
      await getGroupingAPI();
    })();

    return (): void => {
      // unmount
      unmounted.current = true;
    };
  }, [getGroupingAPI]);

  useEffect(() => {
    if (brokerItems && user) {
      disableLoading();
    }
  }, [brokerItems, disableLoading, user]);

  useEffect(() => {
    // anchor link で遷移した場合、react(react-routerなのかも)の影響でコンテンツまでスクロールしないので、この形を取っている
    const elem = document.getElementById(window.location.hash.replace('#', ''));
    if (elem != null) {
      setTimeout(() => elem.scrollIntoView(), 0);
    }

    setAction(() => (type: types.HeaderActionType): void => {
      switch (type) {
        case consts.HeaderActionType.追加:
          history.push('/securityaccountslink');
          break;
      }
    });
  }, [brokerItems, history, setAction]);

  const onBrokerClick = (userBroker: GetUserBrokerResultItem): void => {
    history.push(`accountdetail/${userBroker.Account}`);
  };

  const doGoMoneytreeSiteOK = (): void => {
    window.open(showGoMoneytreeSiteDialogUrl, '_blank');

    setShowGoMoneytreeSiteDialogUrl('');
  };

  const refresh = (): void => {
    if (!beInRefresh) {
      setBeInRefresh(true);
      refreshMoneytree().then(ret => {
        getUserBroker().then(ret => {
          !unmounted.current && setBeInRefresh(false);
          !unmounted.current && ret && setBrokerItems(ret.Items);
        });
        (async () => {
          await getGroupingAPI();
          setExtractedMoneytree(false);
        })();
      });
    }
  };

  const messageView = C_UpdateDataInfo.message.map((value, index) => {
    return value === '\r\n' ? <br key={index} /> : <p key={index}>{value}</p>;
  });

  const updDateTime = brokerItems?.reduce(
    (prev, current) => (prev.UpdTime < current.UpdTime ? current : prev),
    { UpdTime: '' }
  )?.UpdTime;

  const expandIcon = extractedMoneytree
    ? 'chevron-down transform rotate-180'
    : 'chevron-down transform rotate-0';

  const maxHeight = calcMaxHeight(extractedMoneytree);

  const securities = securityCompanies.map((item, index) => {
    return (
      <div className="ui-border-b p-2" key={index}>
        <div className="ml-4 mr-2 flex items-center py-2">
          <div className="flex-grow">
            <div className="text-lg font-bold">{item.name}</div>
          </div>
          <a
            id="securityAccounts_accoutOpening"
            href={`${item.url}${user?.ID}`}
            target="_blank"
            rel="noopener noreferrer">
            <div className="flex py-1 px-2 border rounded border-link text-link bg-white">
              <span className="text-sm">口座開設</span>
              <Icon name="external-link" className="ml-2 text-lg" />
            </div>
          </a>
        </div>
      </div>
    );
  });

  const onClickInfo: MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation();
    showInformationFromMoneytreeModal();
  };

  const location = useLocation();
  if (!isLoaded(groupingState)) return null;

  const isLongPeriodNotLogin = brokerItems?.some(
    brokerItem => brokerItem.Status === '900'
  );

  return (
    <AboutExchangeRateModalProvider>
      <div className="mt-4 mx-2">
        {isLongPeriodNotLogin && (
          <div className="flex w-full px-5 py-2 mb-4 rounded-lg text-sm bg-red-30">
            <div className="icon-alert items-start mr-2 mt-1 text-upcolor"></div>
            <div>更新に失敗しました</div>
            <div
              className="ml-auto text-link cursor-pointer"
              onClick={() => showAccountUpdateStoppedModal()}>
              詳細を確認
            </div>
          </div>
        )}

        {brokerItems && brokerItems.length !== 0 && (
          <div className="flex flex-col justify-end items-baseline pb-3 cursor-pointer">
            <div
              className="flex ml-auto text-sm text-bluegray-500"
              onClick={(): void => showAccountAggregatingModal()}>
              データ更新のタイミングについて
              <Icon
                name="info"
                className="text-lg text-bluegray-400 icon-info ml-1 mr-4"
              />
            </div>
          </div>
        )}
        {user && !user.LinkedLine && isPremium() && (
          <LineNoticeBanner {...{ className: ['mb-2', 'w-1/2', 'w-3/4'] }} />
        )}
        <UserBrokerList
          groupingState={groupingState}
          onBrokerClick={onBrokerClick}
          brokerItems={brokerItems}
          editable={false}
        />

        {!isIosAppNotPremium() && (
          <>
            {user && user?.LinkedMoneytree ? (
              user.AuthenticatedMoneytree ? (
                <div className="ui-card text-gray-800">
                  <div className="p-4 pr-6 border-b border-gray-200">
                    <div className="flex justify-center">
                      <div className="flex flex-col flex-grow">
                        <span className="text-lg font-bold">Moneytree連携</span>
                        <div className="mt-1 flex items-center">
                          <div
                            className="text-sm text-white px-2 rounded-full"
                            style={{
                              backgroundColor: '#14B8A6',
                            }}>
                            連携中
                          </div>
                          <div className="ml-2 text-sm text-bluegray-500 cursor-pointer">
                            {formatDateForString(
                              updDateTime,
                              'yyyy/MM/dd HH:mm'
                            )}
                            &nbsp;最終更新
                          </div>
                        </div>
                      </div>
                      <div
                        className="flex flex-col items-center justify-center text-bluegray-500 cursor-pointer"
                        onClick={refresh}>
                        <Icon
                          name="reflesh"
                          className={`text-xl ${
                            beInRefresh
                              ? 'animation animation-infinite animation-rotate'
                              : ''
                          }`}
                        />
                        <span className="mt-1 text-sm">更新</span>
                      </div>
                    </div>
                    <div
                      className="flex items-center justify-start mt-4 cursor-pointer"
                      onClick={onClickInfo}>
                      <Icon
                        name="info"
                        className="text-bluegray-400 text-base"
                      />
                      <span className="text-bluegray-500 ml-2">
                        取得できる情報
                      </span>
                    </div>
                  </div>
                  <div
                    className={`transition-all duration-300 ${
                      extractedMoneytree
                        ? 'ui-effect-fade-in-300 visible'
                        : 'ui-effect-fade-out-300 invisible'
                    }`}
                    style={{ maxHeight: `${maxHeight}` }}>
                    <div
                      id="moneytree_panel"
                      className="pb-4 border-b border-gray-200">
                      <div className="py-4 border-b border-gray-200 flex">
                        <div
                          className="ml-4 flex-grow cursor-pointer"
                          onClick={(): void =>
                            setShowGoMoneytreeSiteDialogUrl(moneytreeSettingURL)
                          }>
                          <span>Moneytreeの設定</span>
                        </div>
                        <div className="flex items-center mr-4">
                          <Icon
                            name="chevron-right"
                            className="text-2xl text-gray-500"
                          />
                        </div>
                      </div>
                      <div className="py-4 border-b border-gray-200 flex">
                        <div
                          className="ml-4 flex-grow cursor-pointer"
                          onClick={(): void =>
                            setShowGoMoneytreeSiteDialogUrl(
                              moneytreeManagementURL
                            )
                          }>
                          <span>金融機関情報の追加・変更</span>
                        </div>
                        <div className="flex items-center mr-4">
                          <Icon
                            name="chevron-right"
                            className="text-2xl text-gray-500"
                          />
                        </div>
                      </div>
                      <div
                        className="mx-4 mt-4 ui-button theme-outline-important cursor-pointer"
                        onClick={(): void => setShownUnlinkConfirmDialog(true)}>
                        <span className="text-sm">連携解除</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className="py-1 flex justify-center items-center cursor-pointer"
                    onClick={(): void =>
                      setExtractedMoneytree(!extractedMoneytree)
                    }>
                    <Icon
                      className="text-2xl text-bluegray-500 transition duration-300"
                      name={expandIcon}
                    />
                  </div>
                </div>
              ) : (
                <Link to="securityaccountslink">
                  <div className="ui-card text-gray-800">
                    <div className="p-4 pr-2 border-b border-gray-200">
                      <div className="flex justify-center items-center">
                        <div className="flex flex-col flex-grow">
                          <span className="text-lg font-bold">
                            Moneytree連携
                          </span>
                          <div className="mt-1 flex items-center">
                            <div className="flex justify-center text-sm text-red">
                              <Icon name="alert" className="text-xl pr-2" />
                              認証が切れました。再度連携を行ってください。
                            </div>
                          </div>
                        </div>
                        <Icon
                          name="chevron-right"
                          className="text-2xl text-gray-500"
                        />
                      </div>
                    </div>
                  </div>
                </Link>
              )
            ) : null}
          </>
        )}

        {!isIosApp() && (
          <>
            {brokerItems && brokerItems.length !== 0 && (
              <div className="flex justify-end items-baseline pb-3 pr-3">
                <div
                  className="flex ml-auto text-sm text-bluegray-500 cursor-pointer"
                  onClick={(): void => setShowModalByEditing(true)}>
                  集計対象について
                  <Icon
                    name="info"
                    className="text-lg text-bluegray-400 ml-1"
                  />
                </div>
              </div>
            )}
          </>
        )}
        <div className="flex justify-end items-baseline pb-3 pr-3">
          <AboutExchangeRateModalButton
            isShowMessage
            className="text-gray-700"
          />
        </div>

        <div
          id="accountAddBtn"
          className="sticky z-10 bottom-0 p-4 bg-op-w80 shadow-fBtn">
          <Link to="securityaccountslink">
            <div className="border rounded border-blue-700 bg-gray-50 flex items-center justify-center py-3">
              <Icon name="plus" className="text-2xl text-blue-700" />
              <span className="ml-2 text-base text-blue-700 font-bold leading-6">
                口座・資産情報を追加する
              </span>
            </div>
          </Link>
        </div>
        <div className="security-accounts-guide">
          <div className="security-accounts-guide-img">
            <img src="./static/images/grouping.svg" alt="" />
          </div>
          <div className="security-accounts-guide-contents">
            <div className="security-accounts-guide-contents-title">
              便利なグループ編集機能を使ってみよう
            </div>
            <div className="security-accounts-guide-contents-note">
              資産ごとにグループにまとめて管理すれば自分のお金がもっと“見える化”できます。
            </div>
            <div className="link-row">
              <Link className="link-primary align-right" to="grouping">
                グループ編集を使ってみる
              </Link>
            </div>
          </div>
        </div>
        <div className="mx-2 mt-8 text-gray-500">
          <div className="flex items-center text-sm font-bold">
            <Icon name="info" className="text-base" />
            <span className="ml-2">{C_UpdateDataInfo.title}</span>
          </div>
          <div className="mt-1 text-xs">{messageView}</div>
        </div>

        <div
          id="openAccount"
          className="mx-1"
          style={{ marginTop: '-58px', paddingTop: '58px' }}
        />
        {!isIosApp() && (
          <div className="ui-card mt-8">
            <div className="p-4 border-b border-gray-200 font-bold">
              証券口座をお持ちでない方
            </div>
            {securities}

            <div className="m-4 text-center">
              <a
                id="securityAccounts_ranking"
                className="text-link text-sm"
                href={`${securityRankingUrl}${user?.ID}`}
                target="_blank"
                rel="noopener noreferrer">
                ネット証券　総合ランキングを見る
              </a>
            </div>
          </div>
        )}

        <div className="h-16" />

        {shownUnlinkConfirmDialog && (
          <OKCanelModal onOK={doUnlinkOK} onCancel={doUnlinkCancel}>
            <div className="p-4">Moneytreeとの連携を解除します。</div>
          </OKCanelModal>
        )}

        {showGoMoneytreeSiteDialogUrl && (
          <GoMoneytreeSiteDialog
            onOK={doGoMoneytreeSiteOK}
            onCancel={(): void => setShowGoMoneytreeSiteDialogUrl('')}
          />
        )}

        {showModalByEditing === true && (
          <MessageModal
            title={'集計対象について'}
            message={['']}
            onClose={(): void => setShowModalByEditing(false)}>
            <div className="text-sm">
              <p className="text-red">
                マネーツリー連携などにより同一の口座を連携した場合はご注意ください。
              </p>
              <img
                className="mt-3"
                src="./static/images/security_account_edit.svg"
                alt="連携イメージ"></img>
              <p className="mt-3">
                同一の口座を連携した場合、資産額が重複して集計されてしまいます。
              </p>
              <p>
                重複で集計されないように「集計対象」とする口座を選んでください。
              </p>
              <p className="mt-6">
                同一の金融機関の口座が複数連携されていない場合は選択する必要はありません。
              </p>
            </div>
          </MessageModal>
        )}
        <InformationFromMoneytreeModal
          hideModal={hideInformationFromMoneytreeModal}
          isShowModal={isShowInformationFromMoneytreeModal}
        />
        <AboutDataUpdateTimingModal
          hide={() => hideAccountAggregatingModal()}
          isShowModal={isShowAccountAggregatingModal}
        />
      </div>
      <AccountUpdateStoppedModal
        redirectPath={`${env.appURL}${location.pathname}`}
        hide={hideAccountUpdateStoppedModal}
        isShow={isShowAccountUpdateStoppedModal}
      />
      <NoticeSettingModal
        {...{
          isShow: isShowNoticeSettingModal,
          hide: hideNoticeSettingModal,
        }}
      />
    </AboutExchangeRateModalProvider>
  );
};

export default SecurityAccounts;
